import apiClient, { currentTokenGetter } from './apiClient';
import type { LumensParam } from '@flobi/artwork-core';

export interface LibraryShader {
  id: string;
  name: string;
  coverUrl?: string;
  signature?: { engine?: string };
}
export interface LibraryShaderDetail extends LibraryShader {
  version: number;
  payload: {
    source?: { kind: string; frag?: string; fragment?: string; code?: string; passes?: (string | null)[] };
    manifest?: LumensParam[];
    preset?: Record<string, unknown>;
    /** The looks its author saved in Lumens, each control's value as Lumens stores it. */
    presets?: { name: string; seed?: number; values: Record<string, unknown> }[];
    /** Where each picture control's saved still is, by control id: what it shows until one is picked. */
    pictures?: Record<string, string>;
    /** The Lumens frame it was saved from: its seed, frame rate and one loop's length. */
    canvas?: { seed?: number; fps?: number; durationFrames?: number; loop?: boolean };
  };
}
async function command<T>(name: string, body: object): Promise<T> {
  if (!currentTokenGetter()) throw Error('Open an Artwork file to browse your Lumens library.');
  try {
    const response = await apiClient.post(`/lumen/lumen.library.${name}`, body);
    if (response.data?.success === false) throw Error(response.data.error?.message || response.data.error || 'Lumens is unavailable');
    return response.data?.data ?? response.data;
  } catch (error) {
    const detail = (error as { response?: { data?: { error?: string | { message?: string } } } }).response?.data?.error;
    throw Error(typeof detail === 'string' ? detail : detail?.message ?? (error instanceof Error ? error.message : 'Could not reach Lumens.'));
  }
}
/** Which shelf of the Lumens Library: the same three its Library page shows. */
export type LibraryShelf = 'owned' | 'shared' | 'workspace';

/** One saved effect as the Library lists it (`lumen.library.items.list`). */
interface LibraryItemCard {
  id: string;
  name: string;
  coverUrl?: string | null;
  coverPreviews?: { thumb?: string | null; medium?: string | null } | null;
}
/** One layer of a saved item, and the code it runs. */
interface LibraryItemPayload {
  canvas?: { seed?: number; fps?: number; durationFrames?: number; loop?: boolean };
  frames?: Record<string, { source?: { kind?: string; frag?: string; fragment?: string; code?: string; passes?: unknown[] }; manifest?: LumensParam[] }>;
  entries?: { id?: string; frameId?: string; seed?: number; values?: Record<string, unknown> }[];
  /** Saved looks: a name, a seed, and each layer's values by its entry id. */
  presets?: { name?: unknown; seed?: unknown; values?: Record<string, Record<string, unknown> | undefined> }[];
}

const cover = (item: LibraryItemCard) => item.coverPreviews?.thumb ?? item.coverUrl ?? undefined;

/**
 * Lumens' Library item as Artwork adds it: one layer that runs one shader or one sketch. Its saved
 * settings are that layer's values. Anything else (a template, a built-in effect, a stack) keeps the
 * engine name that says why it cannot be added.
 */
export function libraryItemDetail(item: LibraryItemCard & { version?: number; currentVersion?: number; payload?: LibraryItemPayload; pictureUrls?: Record<string, Record<string, string>> }): LibraryShaderDetail {
  const entries = item.payload?.entries ?? [];
  const frame = entries.length === 1 && entries[0]!.frameId ? item.payload?.frames?.[entries[0]!.frameId] : undefined;
  const engine = entries.length > 1 ? 'composite' : frame?.source?.kind ?? 'composite';
  const values = entries[0]?.values ?? {};
  const seed = entries[0]?.seed;
  return {
    id: item.id,
    name: item.name,
    ...(cover(item) ? { coverUrl: cover(item) } : {}),
    signature: { engine },
    version: item.version ?? item.currentVersion ?? 1,
    payload: {
      ...(frame?.source ? { source: { kind: engine, ...(frame.source.frag ? { frag: frame.source.frag } : {}), ...(frame.source.fragment ? { fragment: frame.source.fragment } : {}), ...(frame.source.code ? { code: frame.source.code } : {}), ...(Array.isArray(frame.source.passes) ? { passes: frame.source.passes.map(p => (typeof p === 'string' ? p : null)) } : {}) } } : {}),
      ...(item.payload?.canvas ? { canvas: { ...(typeof item.payload.canvas.seed === 'number' ? { seed: item.payload.canvas.seed } : {}), ...(typeof item.payload.canvas.fps === 'number' ? { fps: item.payload.canvas.fps } : {}), ...(typeof item.payload.canvas.durationFrames === 'number' ? { durationFrames: item.payload.canvas.durationFrames } : {}), ...(typeof item.payload.canvas.loop === 'boolean' ? { loop: item.payload.canvas.loop } : {}) } } : {}),
      ...(frame?.manifest ? { manifest: frame.manifest } : {}),
      preset: seed === undefined || 'seed' in values ? values : { ...values, seed },
      ...(presetsOf(item.payload, entries[0]?.id) ? { presets: presetsOf(item.payload, entries[0]?.id)! } : {}),
      ...(entries[0]?.id && item.pictureUrls?.[entries[0].id] && Object.keys(item.pictureUrls[entries[0].id]!).length ? { pictures: item.pictureUrls[entries[0].id]! } : {}),
    },
  };
}

/** The looks saved with an item, for its one layer. */
function presetsOf(payload: LibraryItemPayload | undefined, entryId: string | undefined): { name: string; seed?: number; values: Record<string, unknown> }[] | null {
  if (!entryId || !Array.isArray(payload?.presets)) return null;
  const out = payload.presets.flatMap((preset) => {
    const values = preset.values?.[entryId];
    if (typeof preset.name !== 'string' || !values) return [];
    return [{ name: preset.name.slice(0, 128), ...(typeof preset.seed === 'number' ? { seed: Math.round(preset.seed) } : {}), values }];
  }).slice(0, 32);
  return out.length ? out : null;
}

export const getLibraryShader = async (itemId: string): Promise<LibraryShaderDetail> => {
  if (!itemId) throw Error('Choose a shader from your Lumens library.');
  return libraryItemDetail(await command<LibraryItemCard & { version?: number; payload?: LibraryItemPayload; pictureUrls?: Record<string, Record<string, string>> }>('items.get', { itemId, withPayload: true }));
};

/**
 * The effects saved in the person's Lumens Library, as soon as the list is in: a name and a cover
 * each. The list does not say what each one runs, so a card of one layer comes back with no engine
 * yet; `libraryEngines` reads them after (cached for the press that follows). Reading every item
 * before showing any held the panel on "Loading Library…" for seconds (Tarek, 3 Nov).
 */
export async function listLibraryShaders(shelf: LibraryShelf, search: string, cursor?: string): Promise<{ items: LibraryShader[]; nextCursor?: string; hasMore: boolean }> {
  const page = await command<{ items: (LibraryItemCard & { layerCount?: number })[]; nextCursor?: string | null; hasMore: boolean }>('items.list', { scope: shelf, type: 'effects', search: search || undefined, cursor, limit: 24 });
  const items = (page.items ?? []).map((card): LibraryShader => ({
    id: card.id,
    name: card.name,
    ...(cover(card) ? { coverUrl: cover(card)! } : {}),
    ...((card.layerCount ?? 1) > 1 ? { signature: { engine: 'composite' } } : {}),
  }));
  return { items, ...(page.nextCursor ? { nextCursor: page.nextCursor } : {}), hasMore: page.hasMore };
}

/** What each listed item runs, a few at a time, handed back as each arrives. */
export function libraryEngines(items: readonly LibraryShader[], onEngine: (id: string, engine: string) => void, isCurrent: () => boolean): void {
  const queue = items.filter(item => !item.signature?.engine);
  const next = async (): Promise<void> => {
    const item = queue.shift();
    if (!item || !isCurrent()) return;
    const engine = await fetchLibraryItem(item.id).then(detail => detail.signature?.engine).catch(() => undefined);
    if (engine && isCurrent()) onEngine(item.id, engine);
    return next();
  };
  for (let lane = 0; lane < 4; lane++) void next();
}

/** Items already asked for, so a press on one a pointer rested on lands at once. */
const fetched = new Map<string, Promise<LibraryShaderDetail>>();
export function fetchLibraryItem(itemId: string): Promise<LibraryShaderDetail> {
  let item = fetched.get(itemId);
  if (!item) {
    item = getLibraryShader(itemId);
    fetched.set(itemId, item);
    item.catch(() => fetched.delete(itemId));
    setTimeout(() => fetched.delete(itemId), 60_000);
  }
  return item;
}
